import { Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  ArrowRight, ShoppingBag, MapPin, Calendar,
  Image as ImageIcon, Film, Tag, Plus, Trash2, CheckCircle2,
} from "lucide-react";

/**
 * Combined section on the home page:
 *  1. "Compte gratuit en 3 clics" hero invitation
 *  2. "Porteur de projet" call-to-action that opens the project submission modal
 */
export default function JoinAndProjectSection() {
  const { user } = useAuth();
  const [projectOpen, setProjectOpen] = useState(false);
  const isAuthed = !!user && user !== false;

  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="join-project-section">
      <div className="grid lg:grid-cols-2 gap-3">
        {/* Free account in 3 clicks — editorial card with photo */}
        <article className="group relative overflow-hidden rounded-3xl bg-white border border-cream-200 shadow-sm hover:shadow-xl transition-shadow flex flex-col">
          <div className="relative h-44 sm:h-52 overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=900&q=70"
              alt="Citoyens partageant un moment ensemble"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-plum-900/55 via-plum-900/15 to-transparent" />
            <div className="absolute top-3 left-3 phenix-pill bg-white/95 text-plum-700 text-[10px]">100 % gratuit · sans CB</div>
          </div>
          <div className="p-6 sm:p-7 flex flex-col flex-1">
            <h2 className="font-display font-extrabold text-[1.5rem] sm:text-[1.75rem] leading-tight text-plum-900 mb-2">
              Créez votre compte en 3 clics
            </h2>
            <p className="text-[14px] text-plum-700/75 leading-relaxed mb-4">
              Aucune carte bancaire, aucun engagement. Juste un pseudo, un email, un mot de passe.
            </p>
            <ol className="space-y-2 mb-5 text-[14px] text-plum-700">
              {[
                "Choisissez votre pseudo",
                "Renseignez email & mot de passe",
                "Profitez : sondages, idées, bons",
              ].map((t, i) => (
                <li key={i} className="flex items-center gap-2.5">
                  <span className="w-6 h-6 rounded-full bg-plum-100 text-plum-700 font-display font-extrabold text-[12px] grid place-items-center shrink-0">
                    {i + 1}
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ol>
            <Link
              to={isAuthed ? "/habitant" : "/login"}
              data-testid="cta-create-account"
              className="mt-auto self-start rounded-full bg-plum-700 hover:bg-plum-800 text-white px-5 py-3 font-display font-bold text-[14px] inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5"
            >
              {isAuthed ? "Voir mon espace" : "Je crée mon compte"} <ArrowRight size={14} />
            </Link>
          </div>
        </article>

        {/* Submit a commerce project — editorial */}
        <article className="group relative overflow-hidden rounded-3xl bg-white border border-cream-200 shadow-sm hover:shadow-xl transition-shadow flex flex-col">
          <div className="relative h-44 sm:h-52 overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=900&q=70"
              alt="Boutique artisanale en centre-ville"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-plum-900/55 via-plum-900/15 to-transparent" />
            <div className="absolute top-3 left-3 phenix-pill bg-sun-400 text-plum-900 text-[10px]">Porteur de projet</div>
          </div>
          <div className="p-6 sm:p-7 flex flex-col flex-1">
            <h2 className="font-display font-extrabold text-[1.5rem] sm:text-[1.75rem] leading-tight text-plum-900 mb-2">
              Une idée de commerce ? Présentez-la aux Istréens.
            </h2>
            <p className="text-[14px] text-plum-700/75 leading-relaxed mb-4">
              Avant de vous lancer, mesurez l&apos;intérêt. Votre projet est soumis à un sondage citoyen modéré par la mairie.
            </p>
            <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-5 text-[12px] text-plum-700/85 font-medium">
              {[
                { icon: ImageIcon, t: "Photo du projet" },
                { icon: Film, t: "Vidéo de présentation" },
                { icon: Tag, t: "Produits & prix" },
                { icon: MapPin, t: "Lieu envisagé" },
                { icon: Calendar, t: "Date d'ouverture" },
                { icon: CheckCircle2, t: "Validation mairie" },
              ].map((it, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <it.icon size={12} strokeWidth={2.4} className="text-plum-500" />
                  {it.t}
                </li>
              ))}
            </ul>
            <button
              onClick={() => setProjectOpen(true)}
              data-testid="cta-submit-project"
              className="mt-auto self-start rounded-full bg-sun-400 hover:bg-sun-500 text-plum-900 px-5 py-3 font-display font-bold text-[14px] inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5"
            >
              Soumettre mon projet <ArrowRight size={14} />
            </button>
          </div>
        </article>
      </div>

      <ProjectSubmissionDialog open={projectOpen} onClose={() => setProjectOpen(false)} />
    </section>
  );
}

function ProjectSubmissionDialog({ open, onClose }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    title: "",
    category: "",
    description: "",
    video_url: "",
    photo_url: "",
    location: "",
    opening_date: "",
    contact_name: "",
    contact_email: "",
  });
  const [products, setProducts] = useState([{ name: "", price: "" }]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateProduct = (i, k, v) => setProducts((arr) => arr.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const addProduct = () => setProducts((arr) => [...arr, { name: "", price: "" }]);
  const removeProduct = (i) => setProducts((arr) => arr.filter((_, idx) => idx !== i));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => update("photo_url", reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.contact_email) {
      return toast.error("Titre, description et email sont obligatoires");
    }
    setBusy(true);
    try {
      await api.post("/projects/", {
        ...form,
        products: products.filter((p) => p.name),
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setDone(false);
    setForm({ title: "", category: "", description: "", video_url: "", photo_url: "", location: "", opening_date: "", contact_name: "", contact_email: "" });
    setProducts([{ name: "", price: "" }]);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-cream-50 border-cream-200 rounded-2xl" data-testid="project-submission-dialog">
        <DialogHeader>
          <div className="phenix-eyebrow inline-flex items-center gap-1.5 mb-1">
            <ShoppingBag size={11} strokeWidth={2.5} /> Soumettre un projet
          </div>
          <DialogTitle className="heading-display text-[1.6rem] text-plum-900">
            Présentez votre projet de commerce
          </DialogTitle>
          <p className="text-[13px] text-plum-700/65">
            Votre demande sera examinée par la mairie sous 5 jours ouvrés avant publication.
          </p>
        </DialogHeader>

        {done ? (
          <div className="text-center py-8" data-testid="project-submitted">
            <div className="w-16 h-16 rounded-full bg-mint-100 text-mint-600 grid place-items-center mx-auto mb-3">
              <CheckCircle2 size={32} strokeWidth={2.2} />
            </div>
            <h3 className="font-display font-extrabold text-xl text-plum-900 mb-1">Projet envoyé !</h3>
            <p className="text-[14px] text-plum-700/75 max-w-md mx-auto leading-snug">
              Merci. La mairie vous recontactera par email après validation. Une fois approuvé, votre projet sera publié sur la home avec un sondage citoyen associé.
            </p>
            <Button onClick={close} className="rounded-full bg-plum-700 hover:bg-plum-800 text-white h-11 px-5 mt-5">Fermer</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Nom du projet *" v={form.title} on={(v) => update("title", v)} placeholder="Ex: Boulangerie artisanale Le Pétrin" testid="proj-title" />
              <Field label="Catégorie" v={form.category} on={(v) => update("category", v)} placeholder="Boulangerie, Bijouterie, Café…" testid="proj-cat" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Description du projet *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                placeholder="Présentez votre concept, votre histoire, ce qui rendra votre commerce unique à Istres…"
                rows={5}
                data-testid="proj-desc"
                className="rounded-xl bg-white"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Vidéo (lien YouTube/Vimeo)" v={form.video_url} on={(v) => update("video_url", v)} placeholder="https://youtu.be/…" testid="proj-video" />
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Photo du projet</Label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={onFile}
                  data-testid="proj-photo"
                  className="block w-full text-[12px] text-plum-700 file:mr-2 file:py-2 file:px-3 file:rounded-full file:border-0 file:bg-plum-100 file:text-plum-700 file:font-semibold hover:file:bg-plum-200"
                />
              </div>
            </div>

            {form.photo_url && (
              <img src={form.photo_url} alt="Aperçu" className="rounded-2xl max-h-44 object-cover w-full" />
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Lieu envisagé" v={form.location} on={(v) => update("location", v)} placeholder="Centre-ville, Place Champollion…" testid="proj-loc" />
              <Field label="Date d'ouverture envisagée" type="date" v={form.opening_date} on={(v) => update("opening_date", v)} testid="proj-date" />
            </div>

            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold flex items-center justify-between">
                Exemples de produits &amp; prix
                <button type="button" onClick={addProduct} className="text-pink-500 text-[11px] inline-flex items-center gap-1 normal-case tracking-normal" data-testid="add-product-btn">
                  <Plus size={11} /> Ajouter
                </button>
              </Label>
              {products.map((p, i) => (
                <div key={i} className="grid grid-cols-[1fr_120px_36px] gap-2">
                  <Input value={p.name} onChange={(e) => updateProduct(i, "name", e.target.value)} placeholder="Nom du produit" className="rounded-xl bg-white h-10" data-testid={`proj-prod-name-${i}`} />
                  <Input value={p.price} onChange={(e) => updateProduct(i, "price", e.target.value)} placeholder="3,50 €" className="rounded-xl bg-white h-10" data-testid={`proj-prod-price-${i}`} />
                  <button type="button" onClick={() => removeProduct(i)} className="text-plum-700/55 hover:text-pink-500 grid place-items-center" aria-label="Supprimer" disabled={products.length === 1}>
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>

            <div className="border-t border-cream-200 pt-3 grid sm:grid-cols-2 gap-3">
              <Field label="Votre nom" v={form.contact_name} on={(v) => update("contact_name", v)} placeholder="Marie Dupont" testid="proj-contact-name" />
              <Field label="Votre email *" type="email" v={form.contact_email} on={(v) => update("contact_email", v)} placeholder="vous@email.fr" testid="proj-contact-email" />
            </div>

            <Button type="submit" disabled={busy} data-testid="proj-submit-btn" className="rounded-full bg-plum-700 hover:bg-plum-800 text-white w-full h-12 mt-2 font-bold inline-flex items-center justify-center gap-2">
              Envoyer mon projet à la mairie <ArrowRight size={14} />
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, type = "text", v, on, placeholder, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">{label}</Label>
      <Input
        type={type}
        value={v}
        onChange={(e) => on(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="rounded-xl h-11 bg-white border-cream-200"
      />
    </div>
  );
}
